import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import { EMAIL_REGEX } from 'morpheus-ui';
import Logo from '../../../assets/images/brand/logo-white.png';
import LogoBlack from '../../../assets/images/brand/logo-black.png';

import useCreateUser from '../hooks/useCreateUser';
import { useLogin } from '../hooks/useLogin';
import { IRegister } from '../models/user.model';
import { toast } from '../../../core/components/atoms/Toast';
import { Typography } from '../../../core/components/molecules/Typography';
import { Alert } from '../../../core/components/atoms/Alert';
import { Input } from '../../../core/components/atoms/Input';
import Checkbox from '../../../core/components/atoms/Checkbox';
import { Button } from '../../../core/components/atoms/Button';


interface IRegisterProps {
  inviteCode?: string;
}

const Register: React.FC<IRegisterProps> = ({ inviteCode }) => {
  const { mutate, isPending } = useCreateUser();
  const { loginUser, isLoading: isLoggingIn } = useLogin();
  
  const [userExists, setUserExists] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const [inputType, setInputType] = useState<{
    password: 'password' | 'text';
    confirmPassword: 'password' | 'text';
  }>({
    password: 'password',
    confirmPassword: 'password',
  });

  const { watch, register, setValue, handleSubmit, formState: { errors }, getValues } = useForm<IRegister>({
    defaultValues: {
      terms: false,
      marketing: false,
      application: 'universitario',
    },
  });

  const onSubmit = (data: IRegister) => {
    delete data.confirmEmail;
    delete data.confirmPassword;

    if (inviteCode) {
      data.invitationCode = inviteCode;
    }

    mutate(data, {
      onError: () => {
        setError(true);
        toast({
          variant: 'error',
          message: 'Não foi possivel realizar o cadastro',
          title: 'Erro!',
        });
      },
      onSuccess: (data) => {
        if (data.userExists) {
          setUserExists(true);
          return;
        }

        loginUser({
          password: getValues('password'),
          email: getValues('email'),
        });
      },
    });
  };

  const phoneValue = watch('phone');

  const changeInputType = (type: keyof typeof inputType) => {
    setInputType((prevType: any) => ({
      ...prevType,
      [type]: prevType[type] === 'password' ? 'text' : 'password',
    }));
  };

  const normalizePhoneNumber = (value: string | undefined) => {
    if (!value) return '';

    return value
      .replace(/[\D]/g, '')
      .replace(/(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{5})(\d)/, '$1-$2')
      .replace(/(-\d{4})(\d+?)/, '$1');
  };

  useEffect(() => {
    setValue('phone', normalizePhoneNumber(phoneValue));
  }, [phoneValue]);

  const terms = watch('terms');
  const marketing = watch('marketing');

  const buttonDisabled = !terms;

  return (
    <div className='md:center flex h-screen flex-col items-center justify-center md:flex-row'>
      {/* Coluna Esquerda (Imagem) */}
      <div className='hidden h-screen items-center justify-center bg-brand-color-primary-dark md:flex md:w-1/2'>
        <img
          src={Logo}
          alt='Imagem de Login'
          className='max-w-xs pb-12 md:pb-0'
        />
      </div>

      {/* Coluna Direita (Formulário) */}
      <div className='h-full w-full justify-center overflow-y-auto md:flex md:w-1/2'>
        <div className='flex justify-center pt-12 md:hidden md:pb-0'>
          <img
            src={LogoBlack}
            alt='Imagem de Login'
            className='z-10 max-w-xs'
          />
        </div>

        <div className='flex justify-center'>
          <div className='z-10 w-full max-w-lg p-6 text-center'>
            <div className='my-8 rounded-lg bg-mono-color-white p-4 shadow-level-4 md:p-8'>
              <div className='flex flex-col gap-xxl'>
                <div className='text-left'>
                  <Typography
                    size='h2'
                    type='title'
                    weight='bold'
                    className='mb-2'
                  >
                    Cadastre-se
                  </Typography>
                  <Typography size='default'>
                    Preencha os dados abaixo para criar <br />
                    seu cadastro no Aprova Universitário
                  </Typography>
                </div>

                {error && (
                  <Alert
                    variant='error'
                    title='Ocorreu um erro ao tentar salvar os dados.'
                    message='Revise os dados e tente novamente.'
                  />
                )}

                {userExists && (
                  <Alert
                    variant='info'
                    title='O e-mail informado já está cadastrado'
                    message='Este email já está cadastrado no sistema.'
                  />
                )}

                <form onSubmit={handleSubmit(onSubmit)} className='text-left'>
                  <div className='mb-4'>
                    <Input
                      {...register('name', {
                        required: 'Digite seu nome.',
                        // Com o pattern nomes como XXXXX da Silva ou XXXXX da Costa também não funcionavam
                        // pattern: {
                        //   value: /^(?!.*\b\w{1,2}\b)[a-zA-Z\s'-]+$/,
                        //   message: 'Nome inválido.',
                        // },
                      })}
                      label='Nome completo'
                      errorMessage={errors.name && errors.name.message}
                      IconLeft={{ type: 'material', Icon: 'account_circle' }}
                    />
                  </div>
                  <div className='mb-4'>
                    <Input
                      {...register('email', {
                        required: 'Digite seu e-mail.',
                        pattern: {
                          value: EMAIL_REGEX,
                          message: 'Digite um e-mail válido.',
                        },
                      })}
                      label='E-mail'
                      errorMessage={errors.email && errors.email.message}
                      IconLeft={{ type: 'material', Icon: 'email' }}
                    />
                  </div>
                  <div className='mb-4'>
                    <Input
                      {...register('confirmEmail', {
                        required: 'Confirme seu e-mail.',
                        pattern: {
                          value: EMAIL_REGEX,
                          message: 'Digite um e-mail válido.',
                        },
                        validate: (value, { email }) => {
                          if (email !== value) {
                            return 'Os e-mails não coincidem.';
                          }
                          return true;
                        },
                      })}
                      label='Confirme seu e-mail'
                      errorMessage={
                        errors.confirmEmail && errors.confirmEmail.message
                      }
                      IconLeft={{ type: 'material', Icon: 'mark_email_read' }}
                    />
                  </div>
                  <div className='mb-4'>
                    <Input
                      {...register('phone', {
                        required: 'Digite seu telefone.',
                        pattern: {
                          value: /^.{14,}$/,
                          message: 'Digite um telefone válido.',
                        },
                      })}
                      label='Telefone'
                      errorMessage={errors.phone && errors.phone.message}
                      IconLeft={{ type: 'material', Icon: 'phone' }}
                    />
                  </div>
                  <div className='mb-4'>
                    <Input
                      {...register('password', {
                        required: true,
                        pattern: {
                          value: /^.{8,}$/,
                          message:
                            'A senha deve ter pelo menos 8 caracteres.',
                        },
                      })}
                      label='Senha'
                      type={inputType.password}
                      errorMessage={
                        errors.password && errors.password.message
                      }
                      IconLeft={{ type: 'material', Icon: 'lock' }}
                      IconRight={{
                        type: 'material',
                        Icon:
                          inputType.password === 'password'
                            ? 'visibility_off'
                            : 'visibility',
                        className: 'cursor-pointer',
                        onClick: () => changeInputType('password'),
                      }}
                    />
                  </div>

                  <div className='mb-4'>
                    <Input
                      {...register('confirmPassword', {
                        required: 'Confirme sua senha.',
                        validate: (value, { password }) => {
                          if (password !== value) {
                            return 'As senhas não coincidem.';
                          }
                          return true;
                        },
                      })}
                      label='Confirme a senha'
                      type={inputType.confirmPassword}
                      errorMessage={
                        errors.confirmPassword &&
                        errors.confirmPassword.message
                      }
                      IconLeft={{ type: 'material', Icon: 'lock' }}
                      IconRight={{
                        type: 'material',
                        Icon:
                          inputType.confirmPassword === 'password'
                            ? 'visibility_off'
                            : 'visibility',
                        className: 'cursor-pointer',
                        onClick: () => changeInputType('confirmPassword'),
                      }}
                    />
                  </div>

                  <div className='mb-4 flex flex-col gap-xs'>
                    <Checkbox
                      id='terms'
                      value={''}
                      checked={terms}
                      variant={'rounded'}
                      onChange={() => setValue('terms', !terms)}
                      label={`Concordo com os <a href='https://www.aprovauniversitario.com.br/termos-de-uso' target='_blank' class='cursor-pointer text-brand-color-primary-pure'> Termos de Uso</a>.`}
                    />  

                    <Checkbox
                      id='terms'
                      value={''}
                      checked={marketing}
                      variant={'rounded'}
                      onChange={() => setValue('marketing', !marketing)}
                      label={`Aceito receber comunicações de marketing.`}
                    />
                  </div>

                  <Button
                    disabled={buttonDisabled}
                    loading={isPending || isLoggingIn}
                    variant='contained'
                    size='large'
                    full
                    className='mt-8'
                  >
                    Cadastrar
                  </Button>
                </form>
              </div>
            </div>

            <Typography
              size='small'
              className='mt-5 flex items-center justify-center pb-8'
            >
              Já possui uma conta?
              <Button
                variant='text'
                size='small'
                asLink
                to={'/'}
              >
                Faça login
              </Button>
            </Typography>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
